import { uiText, useUiLanguage } from '@/i18n/interface';

import React, { useState } from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { FormData } from '../../utils/formValidation';
import { toast } from "@/hooks/use-toast";
import { useSecureAuth } from '../../hooks/useSecureAuth';

interface AdminCodeSectionProps {
  formData: FormData;
  updateFormData: (field: string, value: string) => void;
}

const AdminCodeSection: React.FC<AdminCodeSectionProps> = ({ formData, updateFormData }) => {
  useUiLanguage();
  const [adminCode, setAdminCode] = useState('');
  const [showAdminOption, setShowAdminOption] = useState(false);
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [codeValidationError, setCodeValidationError] = useState('');
  const { validateAdminCodeSecure, logSecurityAttempt } = useSecureAuth();

  const validateAdminCode = async () => {
    if (!adminCode.trim()) {
      setCodeValidationError('Please enter an admin code');
      return;
    }

    setIsValidatingCode(true);
    setCodeValidationError('');

    try {
      const result = await validateAdminCodeSecure(adminCode, formData.email);

      if (result.success) {
        setShowAdminOption(true);
        updateFormData('role', 'admin');
        toast({
          title: uiText("Admin Code Validated"),
          description: uiText("You can now register as an administrator."),
        });

        // Log successful validation
        await logSecurityAttempt({
          type: 'admin_code',
          success: true,
          email: formData.email
        });
      } else {
        setCodeValidationError(result.error || 'Invalid admin code');
        setShowAdminOption(false);
        updateFormData('role', 'student');

        // Log failed validation
        await logSecurityAttempt({
          type: 'admin_code',
          success: false,
          email: formData.email,
          metadata: { error: result.error }
        });
      }
    } catch (error) {
      console.error('Admin code validation error:', error);
      setCodeValidationError('Failed to validate admin code. Please try again.');
      setShowAdminOption(false);
      updateFormData('role', 'student');
    } finally {
      setIsValidatingCode(false);
    }
  };

  const handleAdminCodeChange = (value: string) => {
    setAdminCode(value);
    setCodeValidationError('');
    if (!value.trim()) {
      setShowAdminOption(false);
      updateFormData('role', 'student');
    }
  };

  return (
    <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
      <div className="space-y-4">
        <div>
          <Label htmlFor="adminCode">{uiText("Secure Admin Access Code (Optional)")}</Label>
          <div className="flex gap-2">
            <Input
              id="adminCode"
              type="password"
              placeholder={uiText("Enter secure admin code (e.g., ADM-xxxxx)")}
              value={adminCode}
              onChange={(e) => handleAdminCodeChange(e.target.value)}
              disabled={isValidatingCode}
            />
            <Button
              type="button"
              onClick={validateAdminCode}
              disabled={!adminCode.trim() || isValidatingCode}
              variant="outline"
            >
              {isValidatingCode ? uiText("Validating...") : uiText("Validate")}
            </Button>
          </div>

          {codeValidationError && (
            <Alert className="mt-2 border-red-200 bg-red-50">
              <AlertDescription className="text-red-800">
                {codeValidationError}
              </AlertDescription>
            </Alert>
          )}

          <p className="text-xs text-gray-500 mt-1">
            {uiText("Leave blank to register as a student. Secure admin codes are provided by existing administrators and expire after use.")}</p>
        </div>

        {showAdminOption && (
          <div>
            <Label htmlFor="role">{uiText("Account Type")}</Label>
            <Select value={formData.role || 'student'} onValueChange={(value) => updateFormData('role', value)}>
              <SelectTrigger>
                <SelectValue placeholder={uiText("Select account type")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="student">{uiText("Student")}</SelectItem>
                <SelectItem value="admin">{uiText("Administrator")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCodeSection;
